import { useState, useLayoutEffect, useRef, useEffect, useCallback } from 'react';
import { VIDEO_AUTOPLAY_OBSERVER_OPTIONS } from '@/core/config/intersectionObserver';
import { useIsDesktop } from '@/core/hooks/useMediaQuery';
import { resolveAndMergeABTestingVariant } from '@/shared/features/ab-testing/utils/abTestingUtils';
import { useAppSelector } from '@/app/store/store';
import { selectAbUid } from '@/shared/features/ab-testing/store/abTestingSlice';
import { AnalyticsBannerLink } from '@/shared/features/analytics/presentation/components';
import { useAnalytics } from '@/shared/features/analytics/presentation/hooks/useAnalytics';
import { useGetUserContextQuery } from '@/shared/features/user-context/infra/adapters/userContextApiSlice';

/**
 * Video Banner Section Props
 * Accepts raw JSON data from API for bgBannerSection
 * Compatible with SectionRenderer pattern
 */
interface VideoBannerProps {
  data?: Record<string, unknown>;
  sectionKey?: string;
  eventLabel?: string;
  priority?: number;
  // Legacy prop name for backward compatibility
  bgBannerSection?: Record<string, unknown> | {
    bgUrl?: string;
    bgUrlNew?: string;
    posterUrl?: string;
    posterUrlNew?: string;
    fwImageUrl?: string;
    fwImageUrlNew?: string;
    fwImageWidth?: string;
    fwImageWidthDesktop?: string;
    fwImagePosition?: string;
    title?: string[];
    titleNew?: string[];
    link?: string;
    linkNew?: string;
    isVideo?: boolean;
    isGradient?: boolean;
    rgbColor?: string;
    hexColor?: string;
    autoplay?: string;
    playsinline?: string;
    loop?: string;
    muted?: string;
    width?: string;
    height?: string;
    shouldRefresh?: boolean;
  };
}

export default function VideoBanner({
  data,
  sectionKey,
  eventLabel: _eventLabel,
  priority: _priority,
  bgBannerSection: legacyBgBannerSection,
}: VideoBannerProps) {
  const AB_UID_WAIT_TIMEOUT_MS = 1000;

  // Support both new pattern (data prop) and legacy pattern (bgBannerSection prop)
  const bgBannerSection = data || legacyBgBannerSection;
  const abUidFromStore = useAppSelector(selectAbUid);
  const { data: userContextData, isLoading: isUserContextLoading, isFetching: isUserContextFetching } =
    useGetUserContextQuery(undefined);
  const { analytics, isInitialized } = useAnalytics();
  const bannerRef = useRef<HTMLElement | null>(null);
  const hasTrackedHoverRef = useRef(false);

  const experimentKey = `home.${sectionKey || 'bgBannerSection'}`;

  const resolveBannerExperiment = useCallback(
    (abUid: string | null) => {
      if (!bgBannerSection) {
        return {
          resolvedBgBannerSection: bgBannerSection as typeof bgBannerSection,
          experimentVariant: 'A',
          videoNameForAnalytics: 'Home Hero Banner',
        };
      }

      const base = bgBannerSection as Record<string, unknown>;
      const { resolved, variantKey } = resolveAndMergeABTestingVariant({
        experimentKey,
        base,
        abUid,
      });

      const titleArr = (resolved as Record<string, unknown>).titleNew ?? (resolved as Record<string, unknown>).title;
      const arr = Array.isArray(titleArr) ? titleArr : undefined;
      const videoName = (arr && arr.length > 0 ? arr[0] : '') || 'Home Hero Banner';

      return {
        resolvedBgBannerSection: resolved,
        experimentVariant: variantKey,
        videoNameForAnalytics: videoName,
      };
    },
    [bgBannerSection, experimentKey]
  );

  const rawVisitorId = (userContextData as Record<string, unknown> | undefined)?.visitorId;
  const abUidFromUserContext =
    typeof rawVisitorId === 'number' && Number.isFinite(rawVisitorId) ? String(rawVisitorId) : null;
  const effectiveAbUid = abUidFromStore ?? abUidFromUserContext;

  // Lock AB variant for this component lifecycle to prevent post-hydration swaps.
  // We only lock after user-context has settled (or when a real visitorId is available),
  // so we don't permanently stick to default variant for logged-in users.
  const [lockedBannerExperiment, setLockedBannerExperiment] = useState<ReturnType<typeof resolveBannerExperiment> | null>(
    null
  );
  const [abResolveTimedOut, setAbResolveTimedOut] = useState(false);

  // If route/section changes, unlock and re-resolve for the new experiment key.
  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setLockedBannerExperiment(null);
      setAbResolveTimedOut(false);
      hasTrackedHoverRef.current = false;
    }, 0);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [experimentKey]);

  // Wait briefly for user-context before falling back to default variant.
  useEffect(() => {
    if (lockedBannerExperiment || abResolveTimedOut) return;
    if (!isUserContextLoading && !isUserContextFetching) return;

    const timeoutId = window.setTimeout(() => {
      setAbResolveTimedOut(true);
    }, AB_UID_WAIT_TIMEOUT_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [lockedBannerExperiment, abResolveTimedOut, isUserContextLoading, isUserContextFetching]);

  // Resolve once per section:
  // - Prefer real visitorId (effectiveAbUid) as soon as available
  // - Otherwise wait for user-context settle (or timeout), then lock default variant
  useEffect(() => {
    if (lockedBannerExperiment) return;
    let timeoutId: number | null = null;

    if (effectiveAbUid) {
      timeoutId = window.setTimeout(() => {
        setLockedBannerExperiment(resolveBannerExperiment(effectiveAbUid));
      }, 0);
    } else if ((!isUserContextLoading && !isUserContextFetching) || abResolveTimedOut) {
      timeoutId = window.setTimeout(() => {
        setLockedBannerExperiment(resolveBannerExperiment(null));
      }, 0);
    }

    return () => {
      if (timeoutId !== null) window.clearTimeout(timeoutId);
    };
  }, [
    effectiveAbUid,
    isUserContextLoading,
    isUserContextFetching,
    abResolveTimedOut,
    lockedBannerExperiment,
    bgBannerSection,
    experimentKey,
    resolveBannerExperiment,
  ]);

  const resolvedBgBannerSection = lockedBannerExperiment?.resolvedBgBannerSection ?? null;
  const experimentVariant = lockedBannerExperiment?.experimentVariant ?? 'A';
  const videoNameForAnalytics = lockedBannerExperiment?.videoNameForAnalytics ?? 'Home Hero Banner';
  const [mounted, setMounted] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Extract video properties early
  const videoUrl = resolvedBgBannerSection
    ? (((resolvedBgBannerSection as Record<string, unknown>).bgUrlNew ||
        (resolvedBgBannerSection as Record<string, unknown>).bgUrl) as string | undefined)
    : undefined;
  const posterUrl = resolvedBgBannerSection
    ? (((resolvedBgBannerSection as Record<string, unknown>).posterUrlNew ||
        (resolvedBgBannerSection as Record<string, unknown>).posterUrl) as string | undefined)
    : undefined;
  const isVideo = resolvedBgBannerSection
    ? (((resolvedBgBannerSection as Record<string, unknown>).isVideo ?? true) as boolean)
    : false;

  const fwImageWidth = resolvedBgBannerSection
    ? (((resolvedBgBannerSection as Record<string, unknown>).fwImageWidth) as string | undefined)
    : undefined;
  const fwImageWidthDesktop = resolvedBgBannerSection
    ? (((resolvedBgBannerSection as Record<string, unknown>).fwImageWidthDesktop) as string | undefined)
    : undefined;
  const fwImagePosition = resolvedBgBannerSection
    ? (((resolvedBgBannerSection as Record<string, unknown>).fwImagePosition) as string | undefined)
    : undefined;
  const fwImageBgUrl = resolvedBgBannerSection
    ? (((resolvedBgBannerSection as Record<string, unknown>).fwImageBgUrl) as string | undefined)
    : undefined;

  const isDesktop = useIsDesktop();
  const fwImageWidthResolved = isDesktop
    ? (fwImageWidthDesktop ?? fwImageWidth ?? '50%')
    : (fwImageWidth ?? '50%');

  // Track hero video view every time the section enters the viewport (only when we have banner data)
  useEffect(() => {
    if (!resolvedBgBannerSection || !bannerRef.current || !analytics || !isInitialized) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            try {
              analytics.trackEvent('Homepage Hero Video Viewed', {
                'Video Name': videoNameForAnalytics,
                'Video Variation': experimentVariant,
                'Visitor Id': abUidFromStore ?? '',
              });
            } catch (error) {
              console.error('Error tracking Homepage Hero Video Viewed:', error);
            }
          }
        });
      },
      VIDEO_AUTOPLAY_OBSERVER_OPTIONS
    );

    observer.observe(bannerRef.current);

    return () => {
      observer.disconnect();
    };
  }, [analytics, isInitialized, resolvedBgBannerSection, videoNameForAnalytics, experimentVariant, abUidFromStore]);

  const handleBannerMouseEnter = () => {
    if (!analytics || !isInitialized || hasTrackedHoverRef.current) return;

    try {
      analytics.trackEvent('Homepage Hero Video Hovered', {
        'Video Name': videoNameForAnalytics,
        'Video Variation': experimentVariant,
        'Visitor Id': abUidFromStore ?? '',
      });
      hasTrackedHoverRef.current = true;
    } catch (error) {
      console.error('Error tracking Homepage Hero Video Hovered:', error);
    }
  };

  // Ensure we only render after hydration to prevent mismatch
  useLayoutEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Required for SSR hydration
    setMounted(true);
  }, []);

  // Handle video loading and play/pause based on visibility
  useEffect(() => {
    if (!mounted || !videoRef.current || !videoUrl) return;

    const video = videoRef.current;

    // Handle video load errors
    const handleError = () => {
      console.error('VideoBanner: video element error', {
        videoUrl,
        mediaError: video.error,
      });
      setVideoError(true);
    };

    const handleCanPlay = () => {
      // Ensure video plays when loaded
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {});
      }
    };

    video.addEventListener('error', handleError);
    video.addEventListener('canplay', handleCanPlay);

    // Try to load the video
    video.load();

    // Handle intersection observer for better performance
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            video.play().catch((error) => {
              console.warn('Video play error:', error);
            });
          } else {
            video.pause();
          }
        });
      },
      VIDEO_AUTOPLAY_OBSERVER_OPTIONS
    );

    observer.observe(video);

    return () => {
      video.removeEventListener('error', handleError);
      video.removeEventListener('canplay', handleCanPlay);
      observer.disconnect();
    };
  }, [mounted, videoUrl]);
  
  // Reset error when the media source changes.
  // This is done asynchronously to satisfy `react-hooks/set-state-in-effect`.
  useEffect(() => {
    if (!videoUrl) return;
    const t = setTimeout(() => {
      setVideoError(false);
    }, 0);
    return () => clearTimeout(t);
  }, [videoUrl]);

  // Preload the LCP video/poster for faster loading
  useEffect(() => {
    // Preload poster image (high priority for LCP)
    if (posterUrl) {
      const posterLink = document.createElement('link');
      posterLink.rel = 'preload';
      posterLink.as = 'image';
      posterLink.href = posterUrl;
      posterLink.setAttribute('fetchpriority', 'high');
      document.head.appendChild(posterLink);
      
      return () => {
        document.head.removeChild(posterLink);
      };
    }
  }, [posterUrl]);

  // Note: Video preloading is handled via the <video> element's preload="auto" attribute
  // Using <link rel="preload" as="video"> is not supported - browsers don't support "video" as a valid as value

  // While AB variant lock is pending, keep a neutral reserved placeholder
  // to avoid rendering default variant first and then swapping.
  if (!lockedBannerExperiment && (isUserContextLoading || isUserContextFetching) && !abResolveTimedOut) {
    return (
      <section className="relative w-full overflow-hidden bg-white">
        <div className="relative w-full overflow-hidden bg-slate-100 aspect-[16/9] md:aspect-[12/5] md:max-h-[70vh]" />
      </section>
    );
  }

  // If no banner data is available, keep the same neutral placeholder shape
  // instead of showing debug copy to avoid visual flicker.
  if (!resolvedBgBannerSection) {
    return (
      <section className="relative w-full overflow-hidden bg-white">
        <div className="relative w-full overflow-hidden bg-slate-100 aspect-[16/9] md:aspect-[12/5] md:max-h-[70vh]" />
      </section>
    );
  }

  // Type-safe extraction with fallbacks
  // Use new fields if available, otherwise fallback to old fields
  const fwImageUrl = resolvedBgBannerSection
    ? (((resolvedBgBannerSection as Record<string, unknown>).fwImageUrlNew ||
        (resolvedBgBannerSection as Record<string, unknown>).fwImageUrl) as string | undefined)
    : undefined;
  const titleArray = resolvedBgBannerSection
    ? (((resolvedBgBannerSection as Record<string, unknown>).titleNew ||
        (resolvedBgBannerSection as Record<string, unknown>).title) as string[] | undefined)
    : undefined;
  const title = titleArray && titleArray.length > 0 ? titleArray[0] : '';
  const link = resolvedBgBannerSection
    ? (((resolvedBgBannerSection as Record<string, unknown>).linkNew ||
        (resolvedBgBannerSection as Record<string, unknown>).link) as string | undefined)
    : undefined;
  const isGradient = resolvedBgBannerSection
    ? (((resolvedBgBannerSection as Record<string, unknown>).isGradient ?? false) as boolean)
    : false;
  const autoplay = (resolvedBgBannerSection as Record<string, unknown>).autoplay as string | undefined;
  const playsinline = (resolvedBgBannerSection as Record<string, unknown>).playsinline as string | undefined;
  const loop = (resolvedBgBannerSection as Record<string, unknown>).loop as string | undefined;
  const muted = (resolvedBgBannerSection as Record<string, unknown>).muted as string | undefined;
  const shouldRefresh = (resolvedBgBannerSection as Record<string, unknown>).shouldRefresh as boolean | undefined;

  return (
    <section
      ref={bannerRef}
      className="relative w-full overflow-hidden bg-white"
      onMouseEnter={handleBannerMouseEnter}
    >
      {/* Video/Image Container */}
      {/* Reserve space up-front to avoid CLS while the video metadata loads */}
      <div className="relative w-full overflow-hidden bg-slate-100 aspect-[16/9] md:aspect-[12/5] md:max-h-[70vh]">
        {/* Make entire section clickable if link exists */}
        {link ? (
          <AnalyticsBannerLink
            to={link}
            bannerName={title || 'Home Hero Banner'}
            bannerType="banner"
            fromNavBar={false}
            experimentVariant={experimentVariant}
            eventName="Homepage Hero Video Clicked"
            eventProperties={{
              'Video Name': videoNameForAnalytics,
              'Video Variation': experimentVariant,
              'Visitor Id': abUidFromStore ?? '',
            }}
            className="absolute inset-0 z-20 cursor-pointer"
            aria-label={title || 'Banner link'}
            onMouseEnter={handleBannerMouseEnter}
            shouldRefresh={shouldRefresh}
          />
        ) : null}

        {/* Poster placeholder (prevents "blank" before video is ready) */}
        {posterUrl ? (
          <img
            src={posterUrl}
            alt=""
            aria-hidden="true"
            fetchPriority="high"
            loading="eager"
            decoding="sync"
            className="absolute inset-0 w-full h-full object-cover pointer-events-none"
            style={{ display: 'block' }}
          />
        ) : null}

        {/* Video Background */}
        {isVideo && videoUrl && !videoError && (
          <video
            ref={videoRef}
            className="absolute inset-0 w-full h-full object-cover pointer-events-none"
            style={{ display: 'block' }}
            autoPlay={!!autoplay}
            loop={!!loop}
            muted={!!muted || true} // Always muted for autoplay to work
            playsInline={!!playsinline || true} // Always playsInline for mobile
            preload="auto"
            // @ts-expect-error fetchPriority is valid but not in React types yet
            fetchPriority="high"
            poster={posterUrl}
            aria-hidden="true"
            onError={(e) => {
              console.error('VideoBanner: video onError event', { videoUrl, event: e });
              setVideoError(true);
            }}
          >
            <source src={videoUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        )}

        {/* Fallback Image if no video or video disabled */}
        {/* (Poster is already rendered above as the stable placeholder.) */}

      {/* Gradient Overlay for Text Readability - bottom aligned, repeat-x (1px wide image) or CSS fallback */}
      {isGradient &&
        (fwImageBgUrl ? (
          <div
            className="absolute inset-x-0 bottom-0 left-0 right-0 h-[200px] pointer-events-none bg-bottom bg-repeat-x"
            style={{
              backgroundImage: `url(${fwImageBgUrl})`,
              backgroundSize: 'auto 200px',
            }}
          />
        ) : (
          <div
            className="absolute inset-x-0 bottom-0 h-[200px] pointer-events-none"
            style={{
              background:
                'linear-gradient(to top, rgba(255,255,255,1) 0%, rgba(255,255,255,0.9) 35%, rgba(255,255,255,0) 100%)',
            }}
          />
        ))}

        {/* Content Overlay - Text/Image at bottom */}
        <div className="absolute inset-0 z-10 flex flex-col justify-end items-center pointer-events-none">
            <div
              className={`flex w-full ${fwImagePosition === 'right' ? 'justify-end' : fwImagePosition === 'center' ? 'justify-center' : 'justify-start'}`}
            >
                {/* If fwImageUrl is empty, render no image and no title */}
                {fwImageUrl ? (
                  <img
                    src={fwImageUrl}
                    alt={title}
                    className="h-auto mb-4"
                    style={{
                      width: fwImageWidthResolved,
                      maxWidth: '600px',
                      objectFit: 'contain',
                    }}
                  />
                ) : null}
            </div>
        </div>
      </div>
    </section>
  );
}

